import React, { useRef, useState } from 'react';
import ArrowDown from '@mui/icons-material/KeyboardArrowDown';
import ArrowUp from '@mui/icons-material/KeyboardArrowUp';
import cx from 'classnames';

import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import styles from './Selector.module.scss';

type TSelector = {
  title: string;
  style?: any;
  className?: string;
  selectedValues: { label: string; value: string }[];
  options: { label: string; value: string }[];
  onSelect: (value: { label: string; value: string }[]) => void;
  singleValueSelector?: boolean;
  withoutCheckBox?: boolean;
  searchable?: boolean;
};

export const PermissionSelector: React.FC<TSelector> = ({
  style,
  className,
  options,
  selectedValues,
  onSelect,
  singleValueSelector,
  withoutCheckBox,
  searchable,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [opened, setOpened] = useState(false);
  const [searchText, setSearchText] = useState('');
  const canViewEmails = useCanViewEmails();
  const displayLabel = (label?: string) => !canViewEmails && label?.includes('@') ? 'User' : label;

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
    setOpened(false);
    setSearchText('');
  });

  const toggleValue = (obj: { label: string; value: string }) => {
    if (singleValueSelector) {
      onSelect([obj]);
      return;
    }
    if (obj.value === ACCESS_TYPE_ENUM.ALL) {
      onSelect([obj]);
      return;
    }
    const newValue = [...selectedValues].filter((v) => v.value !== ACCESS_TYPE_ENUM.ALL);
    const index = newValue.findIndex((o) => o.value === obj.value);
    if (index === -1) {
      newValue.push(obj);
    } else {
      newValue.splice(index, 1);
    }
    onSelect(newValue.length === 0 ? [{ label: 'All', value: ACCESS_TYPE_ENUM.ALL }] : newValue);
  };

  return (
    <div ref={ref} onClick={() => setOpened(!opened)} className={cx(styles.container, className)}>
      <div className={styles.title}>
        <span>{selectedValues?.length > 1 ? `${selectedValues?.length} Selected` : displayLabel(selectedValues[0]?.label)}</span>{' '}
        {opened ? <ArrowUp /> : <ArrowDown />}
      </div>
      {opened && (
        <>
          <div className={styles.selector} style={style && { ...style }}>
            {options
              .filter((o) => o.label.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))
              .map((op, i) => (
                <div
                  className={cx(styles.option, !!selectedValues.find((s) => s.value === op.value) ? styles.selectedOption : '')}
                  key={`${op.label}_${op.value}_${i}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(op);
                    if (searchText) {
                      setSearchText('');
                    }
                  }}
                  style={op.value === ACCESS_TYPE_ENUM.ALL ? { borderBottom: '1px solid #39b3ea' } : {}}
                >
                  <span>{displayLabel(op.label)}</span>
                  {!withoutCheckBox && (
                    <input
                      style={{ height: '25px', accentColor: '#39b3ea' }}
                      type="checkbox"
                      checked={!!selectedValues.find((s) => s.value === op.value)}
                      className={styles.checkbox}
                    />
                  )}
                </div>
              ))}
          </div>
          {searchable && (
            <input
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              placeholder="Search user"
              onClick={(e) => e.stopPropagation()}
              className={styles.search}
            />
          )}
        </>
      )}
    </div>
  );
};
